import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { equipmentInfo } from "../../../services/EquipmentApi";
import DashboardLayout from "../../Layout/DashboardLayout";
import { holidayInfo } from "../../../services/holidayAPI";
import CustomCalendarPage from "../CustomCalendar";
import EquipmentModal from "../../Modal/EquipmentModal";
import { equipInfoData } from "./equipInfoData";
import EquipmentViewModal from "../../Modal/EquipmentViewModal";
import { eventInfo } from "../../../services/EventApi";
import { zeroRemover } from "../../../utils/dateformatter";
import EquipmentDeleteModal from "../../Modal/EquipmentDeleteModal";
import { toast } from "react-toastify";

const Equipmentinfo = () => {
  const year = new Date().getFullYear();
  const isLoggedIn = !!sessionStorage.getItem("session_key");

  const [plantId, setPlantId] = useState("MG_PLANT_1");
  const [equipment, setEquipment] = useState(null);
  const [event, setEvent] = useState(null);
  const [locationData, setLocationData] = useState({});
  const [pendingSwitches, setPendingSwitches] = useState({});
  const [holidayMap, setHolidayMap] = useState({});

  const [pageLoading, setPageLoading] = useState(true);
  const [isHolidayLoading, setIsHolidayLoading] = useState(false);
  const [isEquipmentLoading, setIsEquipmentLoading] = useState(false);
  const [isEventLoading, setIsEventLoading] = useState(false);
  const [openEqModal, setOpenEqModal] = useState(false);
  const [openEqViewModal, setOpenEqViewModal] = useState(false);
  const [openEqDeleteModal, setOpenEqDeleteModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState("");
  const [selectedEquip, setSelectedEquip] = useState("");
  const [activeIndex, setActiveIndex] = useState("");
  const [activeEditIndex, setActiveEditIndex] = useState("");
  const [activeViewIndex, setActiveViewIndex] = useState("");
  const [modalPos, setModalPos] = useState({ top: 0, left: 0 });
  const [arrowTop, setArrowTop] = useState(0);
  const [activeColors, setActiveColor] = useState("#000");
  const [modalMode, setModalMode] = useState("");

  const fetchEquipmentInfo = async () => {
    setIsEquipmentLoading(true);
    try {
      const response = await equipmentInfo({
        system_id: Number(sessionStorage.getItem("system_id")) || 2,
        operation_type: "get",
      });
      if (response?.data) {
        setEquipment(response.data);
      } else {
        setEquipment(null);
      }
    } catch (error) {
      console.error("Error fetching equipment info:", error);
      setEquipment(null);
    } finally {
      setIsEquipmentLoading(false);
    }
  };

  const fetchEventList = async () => {
    setIsEventLoading(true);
    try {
      const response = await eventInfo({
        request_type: "info",
        plant_id: "MG_PLANT_1",
      });
      if (response?.data) {
        setEvent(response.data.items);
      } else {
        setEvent(null);
      }
    } catch (error) {
      toast.error(error.response.data.message);
      setEvent(null);
    } finally {
      setIsEventLoading(false);
    }
  };

  const parseHolidayResponse = (items) => {
    const map = {};
    Object.values(items)
      .flat()
      .forEach((h) => {
        const key = h.date.replace(/\//g, "-");
        map[key] = h.info;
      });
    return map;
  };

  const fetchHolidayInfoLocal = async (year, plantId) => {
    setIsHolidayLoading(true);
    try {
      const nextYear = year + 1;
      const [currentYearResp, nextYearResp] = await Promise.all([
        holidayInfo({
          date: String(year),
          request_type: "info",
          plant_id: plantId,
        }),
        holidayInfo({
          date: String(nextYear),
          request_type: "info",
          plant_id: plantId,
        }),
      ]);

      const currentYearHolidays = currentYearResp?.data?.items
        ? parseHolidayResponse(currentYearResp.data.items)
        : {};
      const nextYearHolidays = nextYearResp?.data?.items
        ? parseHolidayResponse(nextYearResp.data.items)
        : {};

      const combinedHolidays = { ...currentYearHolidays, ...nextYearHolidays };

      setHolidayMap(combinedHolidays);
      setPlantId(currentYearResp.data.plant_id);
    } catch (error) {
      console.error("Holiday error:", error);
      setHolidayMap({});
    } finally {
      setIsHolidayLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setPageLoading(false);
      return;
    }

    const loadAll = async () => {
      setPageLoading(true);

      try {
        await Promise.all([
          fetchEquipmentInfo(),
          fetchEventList(),
          fetchHolidayInfoLocal(year, plantId),
        ]);
      } catch (e) {
        console.error("Error during initial load:", e);
      } finally {
        setPageLoading(false);
      }
    };

    loadAll();
  }, [isLoggedIn]);

  const handleSwitchChange = (type, key) => {
    const originalValue = equipment[type][key];
    const currentPendingValue = pendingSwitches[key]?.value ?? originalValue;
    const newValue = !currentPendingValue;

    setPendingSwitches((prev) => {
      const updated = { ...prev };

      if (newValue === originalValue) {
        delete updated[key];
      } else {
        updated[key] = { type, value: newValue, originalValue };
      }

      return updated;
    });
  };

  const handleAdd = () => {
    setOpenEqModal(true);
  };

  const handleEdit = (e, data, index) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setModalPos({ left: rect.x + 75 });

    setArrowTop(rect.top + rect.height / 2);
    setOpenEqModal(true);
    setSelectedEquip(data);
    setActiveIndex(index);
    setActiveEditIndex(index);
    setModalMode("edit");
  };

  const handleOpenView = (e, data, index, color) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setModalPos({ left: rect.x + 75 });

    setArrowTop(rect.top + rect.height / 2);

    setSelectedEquip(data);
    setActiveIndex(index);
    setOpenEqViewModal(true);
    setActiveViewIndex(index);
    setActiveColor(color);
    setModalMode("view");
  };

  const handleOpenDelete = (data, index, color) => {
    setSelectedEquip(data);
    setActiveIndex(index);
    setOpenEqDeleteModal(true);
    setActiveColor(color);
  };

  const handleCloseView = () => {
    setSelectedEquip("");
    setActiveIndex("");
    setActiveViewIndex("");
    setActiveEditIndex("");
    setOpenEqModal(false);
    setOpenEqViewModal(false);
    setOpenEqDeleteModal(false);
    setModalMode("");
  };

  const handleReload = () => {
    fetchEventList();
  };

  const handleConfirmAll = async () => {
    const updatedEquipment = { ...equipment };
    Object.keys(pendingSwitches).forEach((key) => {
      const { type, value } = pendingSwitches[key];
      updatedEquipment[type][key] = value;
    });

    setEquipment(updatedEquipment);
    setPendingSwitches({});

    const PvPayload = {};
    Object.keys(updatedEquipment.mg_pv_pcs).forEach((key) => {
      PvPayload[key] = updatedEquipment.mg_pv_pcs[key];
    });

    const BtPayload = {};
    Object.keys(updatedEquipment.mg_bt_pcs).forEach((key) => {
      BtPayload[key] = updatedEquipment.mg_bt_pcs[key];
    });

    const payload = {
      system_id: String(sessionStorage.getItem("system_id") || "2"),
      operation_type: "set",
      all_items: {
        mg_pv_pcs: PvPayload,
        mg_bt_pcs: BtPayload,
      },
    };

    try {
      const response = await equipmentInfo(payload);

      await fetchEquipmentInfo();
    } catch (error) {
      console.error("Error updating equipment:", error);
    }
  };

  const handleCancelAll = () => {
    setPendingSwitches({});
  };

  const renderSwitches = (items = {}, order, type) => {
    return order.map((key) => {
      const checked = pendingSwitches[key]?.value ?? items[key];

      return (
        <Col key={key} className="mb-2">
          <div className="infoCheckForm px-3 info-row">
            <span>{key.replace("_ON", "")}</span>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span className="label1">待機</span>
              <FormGroup switch>
                <Input
                  type="switch"
                  checked={!!checked}
                  onChange={() => handleSwitchChange(type, key)}
                />
                <Label check>稼働</Label>
              </FormGroup>
            </div>
          </div>
        </Col>
      );
    });
  };

  const pvOrder = [
    "PV-PCS01_ON",
    "PV-PCS02_ON",
    "PV-PCS03_ON",
    "PV-PCS04_ON",
    "PV-PCS05_ON",
    "PV-PCS06_ON",
    "PV-PCS07_ON",
    "PV-PCS08_ON",
    "PV-PCS09_ON",
    "PV-PCS10_ON",
  ];

  const btOrder = ["BT-PCS01_ON", "BT-PCS02_ON", "BT-PCS03_ON", "BT-PCS04_ON"];

  if (pageLoading) {
    return (
      <DashboardLayout
        setLocationData={setLocationData}
        locationData={locationData}
      >
        <div
          className="loading"
          style={{
            height: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="loader"></div>
        </div>
      </DashboardLayout>
    );
  }

  const colors = [
    "#EFEC3AB3",
    "#00F1B5B3",
    "#E26EFFB3",
    "#FFA928B3",
    "#00FF40B3",
  ];
  const sortedEvents = event
    ? [...event].sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    : [];

  const eventColorMap = {};
  sortedEvents.forEach((ev, index) => {
    eventColorMap[String(ev.event_id)] = colors[index % colors.length];
  });

  const dateColorMap = {};
  sortedEvents.forEach((ev) => {
    const startDate = new Date(ev.start_time);
    const key = `${startDate.getFullYear()}-${String(
      startDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`;

    dateColorMap[key] = eventColorMap[String(ev.event_id)];
  });

  return (
    <>
      <DashboardLayout
        setLocationData={setLocationData}
        locationData={locationData}
      >
        <Container fluid className="px-5">
          <div className="d-flex justify-content-between mt-3">
            <div className="equipWidth1 ">
              {isEquipmentLoading ? (
                <div className="loading">
                  <div className="loader"></div>
                </div>
              ) : (
                equipment && (
                  <>
                    <div className="d-flex gap-4 gap-xxl-3">
                      <div>
                        <Row className=" mt-3 align-items-center infoForm  final-row">
                          <Col className="d-flex justify-content-between align-items-center">
                            <span>マイクログリッド機器情報</span>
                            <div>
                              <Button
                                className="displayButton btn-sm"
                                onClick={fetchEquipmentInfo}
                              >
                                再読み込み
                              </Button>
                            </div>
                          </Col>
                        </Row>
                        <Row className="equipInfo mt-3">
                          <Col xl={6} className="px-1 ">
                            {renderSwitches(
                              equipment.mg_pv_pcs,
                              pvOrder.slice(0, 5),
                              "mg_pv_pcs",
                            )}
                          </Col>
                          <Col xl={6} className="px-1">
                            {renderSwitches(
                              equipment.mg_pv_pcs,
                              pvOrder.slice(5, 10),
                              "mg_pv_pcs",
                            )}
                          </Col>
                        </Row>

                        <Row className="equipInfo mt-3">
                          <Col xl={6} className="px-1">
                            {renderSwitches(
                              equipment.mg_bt_pcs,
                              btOrder.slice(0, 2),
                              "mg_bt_pcs",
                            )}
                          </Col>
                          <Col xl={6} className="px-1">
                            {renderSwitches(
                              equipment.mg_bt_pcs,
                              btOrder.slice(2, 4),
                              "mg_bt_pcs",
                            )}
                          </Col>
                        </Row>

                        <Row className="equipInfo mt-3 align-items-center info-row">
                          <Col className="d-flex justify-content-between align-items-center">
                            <span className="dateSpan">
                              最終更新日時 - {equipment.updated_at || "N/A"}{" "}
                              (JST){" "}
                            </span>

                            {Object.keys(pendingSwitches).length > 0 && (
                              <div className="d-flex ">
                                <Button
                                  size="sm"
                                  className="me-2 set-button cancel "
                                  onClick={handleCancelAll}
                                >
                                  キャンセル
                                </Button>

                                <Button
                                  size="sm"
                                  className="set-button "
                                  onClick={handleConfirmAll}
                                >
                                  更新
                                </Button>
                              </div>
                            )}
                          </Col>
                        </Row>
                        <hr className="hrLine"></hr>

                        <Row className=" mt-1 align-items-center infoForm final-row">
                          <Col className="d-flex justify-content-between align-items-center">
                            <span>今後のイベント</span>
                            <div className="d-flex">
                              <Button
                                size="sm"
                                className="me-2 set-button1 add "
                                onClick={handleAdd}
                              >
                                追加
                              </Button>

                              <Button
                                size="sm"
                                className="set-button1 "
                                onClick={handleReload}
                              >
                                再読み込み
                              </Button>
                            </div>
                          </Col>
                        </Row>

                        {isEventLoading ? (
                          <div className="loadingSpin">
                            <Spinner size="md" />
                          </div>
                        ) : (
                          <div className="one">
                            {sortedEvents.map((data, i) => (
                              <Row
                                key={i}
                                className={`mainRow ${i % 2 === 0 ? "row1" : "row2"}`}
                                onMouseEnter={() => setHoveredIndex(i)}
                                onMouseLeave={() => setHoveredIndex(null)}
                              >
                                <Col
                                  xs={6}
                                  style={{
                                    backgroundColor: colors[i % colors.length],
                                  }}
                                  className={`dateRow ${hoveredIndex === i || activeIndex === i ? "dim" : ""}`}
                                >
                                  <span>{zeroRemover(data?.start_time)}</span>~
                                  <span>{zeroRemover(data?.end_time)}</span>
                                </Col>
                                <Col xs={6} className="eventCol">
                                  <div
                                    className={`nameRow ${hoveredIndex === i || activeIndex === i ? "dim" : ""}`}
                                  >
                                    {data.title}
                                  </div>

                                  {(hoveredIndex === i ||
                                    activeIndex === i) && (
                                    <div className="btnRow">
                                      <Button
                                        className={`btn-sm btnEqRow eyeBtn ${activeViewIndex === i ? "eye" : ""}`}
                                        style={{
                                          // "--arrow-color": activeColors,
                                          "--arrow-color": "#fff",
                                        }}
                                        onClick={(e) =>
                                          handleOpenView(
                                            e,
                                            data,
                                            i,
                                            eventColorMap[
                                              String(data.event_id)
                                            ],
                                          )
                                        }
                                      >
                                        <img src="./Eye.png" />
                                      </Button>
                                      <Button
                                        className={`btn-sm btnEqRow penBtn ${activeEditIndex === i ? "pen" : ""}`}
                                        onClick={(e) => handleEdit(e, data, i)}
                                      >
                                        <img src="./Pen tool.png" />
                                      </Button>
                                      <Button
                                        className="btn-sm btnEqRow delBtn"
                                        onClick={() =>
                                          handleOpenDelete(
                                            data,
                                            i,
                                            eventColorMap[
                                              String(data.event_id)
                                            ],
                                          )
                                        }
                                      >
                                        <img src="./delete.png" />
                                      </Button>
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="newVLine d-none d-lg-block ms-xxl-2"></div>
                    </div>
                  </>
                )
              )}
            </div>

            <div className="calendarWidth1 ">
              <CustomCalendarPage
                setHolidayMap={setHolidayMap}
                holidayMap={holidayMap}
                isHolidayLoading={isHolidayLoading}
                fetchHolidayInfo={fetchHolidayInfoLocal}
                event={event}
                plantIdFromApi={plantId}
                eventColorMap={eventColorMap}
              />
            </div>
          </div>
        </Container>
      </DashboardLayout>

      <EquipmentModal
        key={`edit-${selectedEquip?.event_id}`}
        isOpen={openEqModal}
        toggle={handleCloseView}
        fetchEventList={fetchEventList}
        data={selectedEquip}
        modalPos={modalPos}
        arrowTop={arrowTop}
      />
      <EquipmentViewModal
        key={`view-${selectedEquip?.event_id}`}
        isOpen={openEqViewModal}
        toggle={handleCloseView}
        data={selectedEquip}
        modalPos={modalPos}
        arrowTop={arrowTop}
        activeColors={activeColors}
      />
      <EquipmentDeleteModal
        isOpen={openEqDeleteModal}
        toggle={handleCloseView}
        data={selectedEquip}
        fetchEventList={fetchEventList}
        activeColors={activeColors}
      />
    </>
  );
};

export default Equipmentinfo;
